import { useQueryClient } from '@tanstack/react-query';
import { UNIT_TYPE_LABELS, VAT_RATE_LABELS, type ProductDTO } from '@bifeai/shared';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@bifeai/ui';
import { Ellipsis, FileSpreadsheet, FolderTree, History, Package, Pencil, Percent, Plus, Tag, Trash2 } from 'lucide-react';
import { useDeferredValue, useState } from 'react';
import { ProductThumb, SearchBox, Toolbar, ViewToggle } from '../../components/inventory';
import { VatDialog } from '../../components/VatDialog';
import { useSession } from '../../lib/session';
import { api, errorMessage } from '../../lib/api';
import { invalidateInventory, useCatalogAccess, useCategories, useProducts } from '../../lib/catalog';
import { formatMoney } from '../../lib/format';
import { formatQty, priceUnit } from '../../lib/units';
import { useViewMode } from '../../lib/view-mode';
import { BulkPriceDialog } from './BulkPriceDialog';
import { CategoriesDialog } from './CategoriesDialog';
import { ImportDialog } from './ImportDialog';
import { PriceDialog, PriceHistoryDialog } from './PriceDialog';
import { ProductDialog } from './ProductDialog';

type Modal =
  | { kind: 'product'; product: ProductDTO | null }
  | { kind: 'price'; product: ProductDTO }
  | { kind: 'history'; product: ProductDTO }
  | { kind: 'archive'; product: ProductDTO }
  | { kind: 'categories' }
  | { kind: 'import' }
  | { kind: 'bulk' }
  | { kind: 'vat' }
  | null;

export function ProductsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const access = useCatalogAccess();
  const { me } = useSession();
  // Con facturación electrónica: se ve y se cambia el IVA de cada producto (lo carga la casa central).
  const showVat = me.tenant.modules.includes('ARCA') && access.canCatalog;
  const [onlyNoVat, setOnlyNoVat] = useState(false);
  const [view, setView] = useViewMode('productos');
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState<Modal>(null);
  const [archiving, setArchiving] = useState(false);
  const deferredSearch = useDeferredValue(search.trim());

  const categories = useCategories();
  const products = useProducts({
    search: deferredSearch || undefined,
    categoryId: categoryId || undefined,
    includeInactive: showInactive,
  });
  const list = (products.data ?? []).filter((p) => !onlyNoVat || (!p.vatRate && !p.isCarcass));
  const filtering = Boolean(deferredSearch || categoryId);

  const close = () => setModal(null);
  const refresh = () => invalidateInventory(qc);

  async function archive(p: ProductDTO) {
    setArchiving(true);
    try {
      const r = await api.del<{ deleted: boolean }>(`/products/${p.id}`);
      toast.success('Producto borrado', r.deleted ? p.name : `${p.name}: sus ventas y compras quedan en el historial.`);
      close();
      await refresh();
    } catch (err) {
      toast.error('No se pudo borrar', errorMessage(err));
    } finally {
      setArchiving(false);
    }
  }

  const open = (p: ProductDTO) => {
    if (access.canCatalog) setModal({ kind: 'product', product: p });
    else if (access.canPrices) setModal({ kind: 'price', product: p });
  };
  const clickable = access.canCatalog || access.canPrices;

  const actionsMenu = (p: ProductDTO) =>
    clickable && (
      <Menu>
        <MenuTrigger asChild>
          <button className="bf-icon-btn" aria-label={`Acciones para ${p.name}`} onClick={(e) => e.stopPropagation()}>
            <Ellipsis />
          </button>
        </MenuTrigger>
        <MenuContent>
          {access.canCatalog && (
            <MenuItem icon={<Pencil />} onSelect={() => setModal({ kind: 'product', product: p })}>
              Editar producto
            </MenuItem>
          )}
          <MenuItem icon={<Tag />} onSelect={() => setModal({ kind: 'price', product: p })}>
            Cambiar precio
          </MenuItem>
          <MenuItem icon={<History />} onSelect={() => setModal({ kind: 'history', product: p })}>
            Historial de precios
          </MenuItem>
          {access.canCatalog && (
            <MenuItem icon={<Trash2 />} danger onSelect={() => setModal({ kind: 'archive', product: p })}>
              Borrar
            </MenuItem>
          )}
        </MenuContent>
      </Menu>
    );

  const priceLabel = (p: ProductDTO) => {
    if (p.priceCents === null) return <span className="bf-text-muted">Sin precio</span>;
    const own = access.priceBranchId && p.branchPrices.some((b) => b.branchId === access.priceBranchId);
    return (
      <>
        {formatMoney(p.priceCents)}
        <small> {priceUnit(p.unitType)}</small>
        {own && access.canGeneralPrice && (
          <>
            {' '}
            <Badge tone="info">Precio propio</Badge>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <PageHeader
        title="Productos"
        subtitle={
          access.canCatalog
            ? 'Tu catálogo con fotos, precios y stock. Lo que cargues acá aparece en el punto de venta.'
            : access.priceBranchName
              ? `Catálogo de la cadena con los precios de ${access.priceBranchName}.`
              : 'Catálogo de la cadena.'
        }
        actions={
          <>
            {access.canCatalog && (
              <>
                <Button icon={<FolderTree />} onClick={() => setModal({ kind: 'categories' })}>
                  Categorías
                </Button>
                <Button icon={<FileSpreadsheet />} onClick={() => setModal({ kind: 'import' })}>
                  Importar Excel
                </Button>
              </>
            )}
            {showVat && (
              <Button icon={<Percent />} onClick={() => setModal({ kind: 'vat' })}>
                IVA
              </Button>
            )}
            {access.canPrices && (
              <Button icon={<Percent />} onClick={() => setModal({ kind: 'bulk' })}>
                Aumento masivo
              </Button>
            )}
            {access.canCatalog && (
              <Button variant="primary" icon={<Plus />} onClick={() => setModal({ kind: 'product', product: null })}>
                Nuevo producto
              </Button>
            )}
          </>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar por nombre o código" />
        <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label="Categoría">
          <option value="">Todas las categorías</option>
          {categories.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.products})
            </option>
          ))}
        </Select>
        <button type="button" className="bf-chip" aria-pressed={showInactive} onClick={() => setShowInactive((v) => !v)}>
          Ver inactivos
        </button>
        {showVat && (
          <button type="button" className="bf-chip" aria-pressed={onlyNoVat} onClick={() => setOnlyNoVat((v) => !v)}>
            Sin IVA
          </button>
        )}
        <span className="bf-toolbar-spacer" />
        <ViewToggle value={view} onChange={setView} />
      </Toolbar>

      {products.isPending ? (
        <div className="bf-product-grid">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} style={{ height: 230, borderRadius: 14 }} />
          ))}
        </div>
      ) : list.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Package />}
            title={filtering ? 'No encontramos productos' : 'Todavía no hay productos'}
            text={
              filtering
                ? 'Probá con otra búsqueda o categoría.'
                : access.canCatalog
                  ? 'Cargalos de a uno o subí tu lista desde Excel en un minuto.'
                  : 'La casa central todavía no cargó el catálogo.'
            }
            action={
              !filtering &&
              access.canCatalog && (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <Button icon={<FileSpreadsheet />} onClick={() => setModal({ kind: 'import' })}>
                    Importar Excel
                  </Button>
                  <Button variant="primary" icon={<Plus />} onClick={() => setModal({ kind: 'product', product: null })}>
                    Nuevo producto
                  </Button>
                </div>
              )
            }
          />
        </Card>
      ) : view === 'grid' ? (
        <div className="bf-product-grid">
          {list.map((p) => (
            <article
              key={p.id}
              className="bf-product-card"
              data-clickable={clickable}
              data-inactive={!p.isActive}
              onClick={() => open(p)}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={(e) => clickable && e.key === 'Enter' && open(p)}
              aria-label={p.name}
            >
              <ProductThumb photoUrl={p.photoUrl} icon={p.categoryIcon} name={p.name} size="lg" />
              <div className="bf-product-card-flags">
                {!p.isActive && <Badge>Inactivo</Badge>}
                {p.plu && <Badge tone="info">PLU {p.plu}</Badge>}
                {showVat && !p.isCarcass && !p.vatRate && <Badge tone="warning">Sin IVA</Badge>}
              </div>
              <div className="bf-product-card-menu">{actionsMenu(p)}</div>
              <div className="bf-product-card-body">
                <span className="bf-product-card-cat">{p.categoryName ?? 'Sin categoría'}</span>
                <span className="bf-product-card-name">{p.name}</span>
                <span className="bf-product-card-price">{priceLabel(p)}</span>
                <div className="bf-product-card-meta">
                  <span>{UNIT_TYPE_LABELS[p.unitType]}</span>
                  <span className={p.stockQty < 0 ? 'bf-text-danger' : p.minStock > 0 && p.stockQty < p.minStock ? 'bf-text-warning' : ''}>
                    Stock: {formatQty(p.unitType, p.stockQty)}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <Card>
          <CardBody flush>
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Categoría</th>
                    <th>Código</th>
                    <th>Precio</th>
                    {showVat && <th>IVA</th>}
                    <th>Stock</th>
                    <th aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.id} data-clickable={clickable} onClick={() => open(p)}>
                      <td>
                        <div className="bf-cell-product">
                          <ProductThumb photoUrl={p.photoUrl} icon={p.categoryIcon} name={p.name} size="sm" />
                          <div>
                            <span className="bf-cell-main">{p.name}</span> {!p.isActive && <Badge>Inactivo</Badge>}
                            <div className="bf-cell-sub">{UNIT_TYPE_LABELS[p.unitType]}</div>
                          </div>
                        </div>
                      </td>
                      <td>{p.categoryName ?? <span className="bf-cell-sub">Sin categoría</span>}</td>
                      <td className="bf-cell-sub">{[p.plu && `PLU ${p.plu}`, p.sku].filter(Boolean).join(' · ') || '—'}</td>
                      <td className="bf-num" style={{ whiteSpace: 'nowrap' }}>
                        {priceLabel(p)}
                      </td>
                      {showVat && (
                        <td>
                          {p.isCarcass ? (
                            <span className="bf-cell-sub">—</span>
                          ) : p.vatRate ? (
                            VAT_RATE_LABELS[p.vatRate]
                          ) : (
                            <Badge tone="warning">Sin IVA</Badge>
                          )}
                        </td>
                      )}
                      <td className={`bf-num ${p.stockQty < 0 ? 'bf-text-danger' : ''}`}>{formatQty(p.unitType, p.stockQty)}</td>
                      <td className="bf-cell-actions" onClick={(e) => e.stopPropagation()}>
                        {actionsMenu(p)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {modal?.kind === 'product' && (
        <ProductDialog
          product={modal.product}
          categories={categories.data ?? []}
          onClose={close}
          onSaved={async (p, isNew, warning) => {
            if (warning) toast.error('Foto sin guardar', warning);
            else toast.success(isNew ? 'Producto creado' : 'Producto actualizado', p.name);
            close();
            await refresh();
          }}
        />
      )}
      {modal?.kind === 'price' && (
        <PriceDialog
          product={modal.product}
          onClose={close}
          onSaved={async (p) => {
            toast.success('Precio actualizado', p.name);
            close();
            await refresh();
          }}
        />
      )}
      {modal?.kind === 'history' && <PriceHistoryDialog product={modal.product} onClose={close} />}
      {modal?.kind === 'categories' && <CategoriesDialog categories={categories.data ?? []} onClose={close} />}
      {modal?.kind === 'import' && (
        <ImportDialog
          onClose={close}
          onDone={async (r) => {
            toast.success(
              'Importación lista',
              `${r.created} nuevos, ${r.updated} actualizados${r.categoriesCreated ? `, ${r.categoriesCreated} categorías nuevas` : ''}.`,
            );
            close();
            await refresh();
          }}
        />
      )}
      {modal?.kind === 'bulk' && (
        <BulkPriceDialog
          categories={categories.data ?? []}
          onClose={close}
          onApplied={async (n) => {
            toast.success('Precios actualizados', `${n} ${n === 1 ? 'producto' : 'productos'}.`);
            close();
            await refresh();
          }}
        />
      )}
      {modal?.kind === 'vat' && <VatDialog onClose={close} />}
      <Dialog
        open={modal?.kind === 'archive'}
        onOpenChange={(o) => !o && close()}
        title="Borrar producto"
        description={`¿Borrar ${modal?.kind === 'archive' ? modal.product.name : ''}? Deja de aparecer en el catálogo y en el punto de venta. Si ya tuvo ventas o compras, esos movimientos quedan en el historial.`}
      >
        <div className="bf-form-actions">
          <Button onClick={close}>Cancelar</Button>
          <Button variant="danger" icon={<Trash2 />} loading={archiving} onClick={() => modal?.kind === 'archive' && void archive(modal.product)}>
            Borrar
          </Button>
        </div>
      </Dialog>
    </>
  );
}
